
import { supabase } from '@/integrations/supabase/client';
import logger from './logger';

// Type for function response
type FunctionResponse<T> = {
  data: T | null;
  error: Error | null;
};

/**
 * Securely invoke Supabase Edge Functions
 * @param functionName The name of the function to invoke
 * @param payload The payload to send to the function
 */
export async function invokeFunction<T>(
  functionName: string, 
  payload?: any
): Promise<FunctionResponse<T>> {
  try {
    // Get the current session to include auth token
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token && functionName !== 'public-function') {
      logger.warn(`No auth token available when calling ${functionName}`);
    }

    // Invoke the function with authentication header
    const { data, error } = await supabase.functions.invoke<T>(functionName, {
      body: payload || {},
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });

    if (error) {
      logger.error(`Error invoking ${functionName}:`, error);
      throw error;
    }
    
    logger.info(`Successfully invoked ${functionName}`);
    return { data, error: null };
  } catch (error) {
    logger.error(`Error invoking function ${functionName}:`, error);
    return { data: null, error: error as Error };
  }
}

/**
 * Helper function to invoke the deduct-listing-credit edge function
 * @param userId The ID of the user to deduct credits from
 */
export async function deductListingCredit(userId: string): Promise<any> {
  try {
    const response = await invokeFunction('deduct-listing-credit', { userId });
    return response;
  } catch (error) {
    logger.error('Error deducting listing credit:', error);
    throw error;
  }
}

// Define the type for the response from the merge-cookie-consent function
type MergeCookieConsentResponse = {
  success: boolean;
  message?: string;
};

/**
 * Helper function to merge cookie consent for a user who gave consent before logging in
 * @param userId The ID of the authenticated user
 * @param ipAddress The IP address previously used for anonymous consent
 */
export async function mergeCookieConsent(userId: string, ipAddress: string): Promise<boolean> {
  try {
    const response = await invokeFunction<MergeCookieConsentResponse>('merge-cookie-consent', { 
      userId,
      ipAddress
    });
    return response.data?.success || false;
  } catch (error) {
    logger.error('Error merging cookie consent:', error);
    return false;
  }
}
